// ExtractionTablePreview.js
import React from 'react';
import {
    Table, TableHead, TableBody, TableRow, TableCell, Box, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const computeSpans = (headers) => {
    if (!headers || !headers.length || !headers[0]?.length) return [];
    const numRows = headers.length;
    const numCols = headers[0].length;
    const skip = Array.from({ length: numRows }, () => Array(numCols).fill(false));
    const result = [];

    for (let row = 0; row < numRows; row++) {
        const rowCells = [];
        for (let col = 0; col < numCols; col++) {
            if (skip[row][col]) continue;
            const value = headers[row][col];
            if (!value) continue;

            let rowSpan = 1;
            let colSpan = 1;

            // vertical
            for (let r = row + 1; r < numRows; r++) {
                if (!headers[r][col]) {
                    rowSpan++;
                    skip[r][col] = true;
                } else break;
            }
            // horizontal: merge across blanks OR identical values
            for (let c = col + 1; c < numCols; c++) {
                const v2 = headers[row][c];

                // Stop if this neighbor isn't mergeable by value rules
                const canMergeByValue = (v2 === '' || v2 === value);
                if (!canMergeByValue) break;

                // Also require that all parent rows (0..row-1) match, so we don't
                // merge across a different group/table/section.
                let sameParents = true;
                for (let pr = 0; pr < row; pr++) {
                    if (headers[pr][col] !== headers[pr][c]) {
                        sameParents = false;
                        break;
                    }
                }
                if (!sameParents) break;

                colSpan++;
                skip[row][c] = true;
            }
            rowCells.push({ value, rowSpan, colSpan });
        }
        result.push(rowCells);
    }
    return result;
};

export default function ExtractionTablePreview({ tableData }) {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    if (!tableData || !Array.isArray(tableData.headers) || tableData.headers.length === 0) {
        return <Typography>No extraction data available.</Typography>;
    }

    const headers = tableData.headers;
    const headerSpans = computeSpans(headers);

    // Prefer multi-row, fall back to single row
    const rows = Array.isArray(tableData.rows) && tableData.rows.length
        ? tableData.rows
        : (Array.isArray(tableData.row) ? [tableData.row] : []);

    const colCount = rows[0]?.length ?? headers[0]?.length ?? 0;
    const columnMeta = Array.isArray(tableData.columnMeta) && tableData.columnMeta.length === colCount
        ? tableData.columnMeta
        : Array.from({ length: colCount }, () => ({ mergeVertical: false }));

    const hasMultipleRows = rows.length > 1;

    return (
        <Box overflow="auto">
            <Table size="small" stickyHeader>
                {!!headerSpans.length && (
                    <TableHead>
                        {headerSpans.map((row, rowIndex) => (
                            <TableRow key={`header-row-${rowIndex}`}>
                                {row.map((cell, cellIndex) => (
                                    <TableCell
                                        key={`header-cell-${rowIndex}-${cellIndex}`}
                                        align="center"
                                        colSpan={cell.colSpan}
                                        rowSpan={cell.rowSpan}
                                        style={{
                                            fontWeight: 'bold',
                                            background: isDarkMode ? '#2c2c2c' : '#f0f0f0',
                                            color: isDarkMode ? theme.palette.text.primary : '#1a1a1a',
                                            border: '1px solid #ddd',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {cell.value}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableHead>
                )}

                <TableBody>
                    {rows.map((r, rIdx) => (
                        <TableRow key={`body-row-${rIdx}`}>
                            {r.map((cell, cIdx) => {
                                const mergeThisCol = hasMultipleRows && columnMeta[cIdx]?.mergeVertical;
                                // For merged columns: render only once (top cell), spanning the rest
                                if (mergeThisCol && rIdx > 0) return null;
                                const rowSpan = mergeThisCol ? rows.length : 1;

                                return (
                                    <TableCell
                                        key={`body-cell-${rIdx}-${cIdx}`}
                                        align="left"
                                        rowSpan={rowSpan}
                                        style={{ border: '1px solid #ddd' }}
                                    >
                                        {cell}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}
